/**
 * Hearth design system — primitives.
 *
 * Composed from semantic tokens (`--color-page`, `--color-card`,
 * `--color-accent`, …) defined in `web/src/app.css`. Each primitive
 * renders correctly in both light and dark themes with no per-component
 * theme branching; the data-theme attribute on <html> drives the
 * variable swap.
 *
 * Conventions:
 *   - Card / StatCard / Pill / ProgressBar / SectionHeader / EmptyState
 *     are layout/content building blocks.
 *   - Button has 3 variants × 4 sizes; `<ConfirmButton>` and `<Toast>`
 *     stay as separate files (existing primitives, restyled in place).
 *   - Form fields wrap label + input + helper + error so pages stop
 *     re-declaring `inputClass` constants.
 *   - All components forward `className` so callers can extend.
 */

import { Children, forwardRef, isValidElement, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { NavLink } from "react-router";

function cx(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(" ");
}

// ─── Card ──────────────────────────────────────────────────────────────

export function Card({
  children,
  className,
  padded = true,
  elevated = false,
}: {
  children: ReactNode;
  className?: string;
  padded?: boolean;
  elevated?: boolean;
}) {
  return (
    <div
      className={cx(
        "rounded-xl border border-[var(--color-border-default)] bg-[var(--color-card)]",
        padded && "p-5",
        elevated && "shadow-[var(--shadow-card)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

// ─── StatCard ──────────────────────────────────────────────────────────

export function StatCard({
  label,
  value,
  sub,
  trend,
  trendDirection,
  className,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  trend?: ReactNode;
  trendDirection?: "up" | "down" | "flat";
  className?: string;
}) {
  return (
    <Card className={cx("flex flex-col gap-1", className)}>
      <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-muted)]">
        {label}
      </p>
      <p className="font-mono text-2xl font-medium tabular-nums text-[var(--color-text)]">
        {value}
      </p>
      {sub && (
        <p className="text-xs text-[var(--color-muted)]">{sub}</p>
      )}
      {trend && (
        <p
          className={cx(
            "mt-1 inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium",
            trendDirection === "up" &&
              "bg-[var(--color-success-soft)] text-[var(--color-success)]",
            trendDirection === "down" &&
              "bg-[var(--color-danger-soft)] text-[var(--color-danger)]",
            (trendDirection === "flat" || !trendDirection) &&
              "bg-[var(--color-elevated)] text-[var(--color-muted)]",
          )}
        >
          {trend}
        </p>
      )}
    </Card>
  );
}

// ─── Pill ──────────────────────────────────────────────────────────────

type PillTone = "neutral" | "accent" | "success" | "danger" | "warning";

export function Pill({
  children,
  tone = "neutral",
  className,
}: {
  children: ReactNode;
  tone?: PillTone;
  className?: string;
}) {
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium",
        tone === "neutral" && "bg-[var(--color-elevated)] text-[var(--color-muted)]",
        tone === "accent" && "bg-[var(--color-accent-soft)] text-[var(--color-accent)]",
        tone === "success" && "bg-[var(--color-success-soft)] text-[var(--color-success)]",
        tone === "danger" && "bg-[var(--color-danger-soft)] text-[var(--color-danger)]",
        tone === "warning" && "bg-[var(--color-warning-soft)] text-[var(--color-warning)]",
        className,
      )}
    >
      {children}
    </span>
  );
}

// ─── ProgressBar ───────────────────────────────────────────────────────

export function ProgressBar({
  value,
  max = 100,
  tone = "accent",
  className,
  ariaLabel,
}: {
  value: number;
  max?: number;
  tone?: "accent" | "success" | "danger" | "warning";
  className?: string;
  ariaLabel?: string;
}) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div
      className={cx("h-2 w-full overflow-hidden rounded-full bg-[var(--color-elevated)]", className)}
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-label={ariaLabel}
    >
      <div
        className={cx(
          "h-full rounded-full transition-[width]",
          tone === "accent" && "bg-[var(--color-accent)]",
          tone === "success" && "bg-[var(--color-success)]",
          tone === "danger" && "bg-[var(--color-danger)]",
          tone === "warning" && "bg-[var(--color-warning)]",
        )}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

// ─── SectionHeader ─────────────────────────────────────────────────────

export function SectionHeader({
  title,
  sub,
  action,
  className,
}: {
  title: ReactNode;
  sub?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <header className={cx("mb-4 flex items-end justify-between gap-3", className)}>
      <div className="min-w-0">
        <h2 className="font-display text-2xl font-medium tracking-tight text-[var(--color-text)]">
          {title}
        </h2>
        {sub && (
          <p className="mt-0.5 text-sm text-[var(--color-muted)]">{sub}</p>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </header>
  );
}

// ─── EmptyState ────────────────────────────────────────────────────────

export function EmptyState({
  title,
  description,
  action,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <Card className={cx("flex flex-col items-center justify-center py-12 text-center", className)}>
      <p className="text-base font-medium text-[var(--color-text)]">{title}</p>
      {description && (
        <p className="mt-1 max-w-md text-sm text-[var(--color-muted)]">{description}</p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </Card>
  );
}

// ─── Button ────────────────────────────────────────────────────────────

type ButtonVariant = "primary" | "secondary" | "ghost" | "destructive";
type ButtonSize = "xs" | "sm" | "md" | "lg";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

const buttonBase =
  "inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-[var(--color-page)]";

const buttonVariants: Record<ButtonVariant, string> = {
  primary:
    "bg-[var(--color-accent)] text-[var(--color-accent-contrast)] hover:bg-[var(--color-accent-hover)]",
  secondary:
    "border border-[var(--color-border-default)] bg-[var(--color-card)] text-[var(--color-text)] hover:bg-[var(--color-elevated)]",
  ghost:
    "text-[var(--color-text)] hover:bg-[var(--color-elevated)]",
  destructive:
    "bg-[var(--color-danger)] text-white hover:opacity-90",
};

const buttonSizes: Record<ButtonSize, string> = {
  xs: "px-2 py-1 text-xs",
  sm: "px-2.5 py-1.5 text-sm",
  md: "px-3 py-2 text-sm",
  lg: "px-4 py-2.5 text-base",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = "primary", size = "md", className, ...rest }, ref) => (
    <button
      ref={ref}
      className={cx(buttonBase, buttonVariants[variant], buttonSizes[size], className)}
      {...rest}
    />
  ),
);
Button.displayName = "Button";

// ─── Form fields ──────────────────────────────────────────────────────

const fieldLabelClass =
  "mb-1 block text-xs font-medium uppercase tracking-wide text-[var(--color-muted)]";
const fieldHelperClass = "mt-1 text-xs text-[var(--color-muted)]";
const fieldErrorClass = "mt-1 text-xs text-[var(--color-danger)]";
const inputBase =
  "block w-full rounded-md border border-[var(--color-border-default)] bg-[var(--color-card)] px-3 py-2 text-sm text-[var(--color-text)] placeholder:text-[var(--color-faint)] focus:border-[var(--color-accent)] focus:outline-none focus:ring-1 focus:ring-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-60";

interface FieldFrameProps {
  label?: ReactNode;
  helper?: ReactNode;
  error?: ReactNode;
  required?: boolean | undefined;
  className?: string | undefined;
  children: ReactNode;
}

export function FieldFrame({
  label,
  helper,
  error,
  required,
  className,
  children,
}: FieldFrameProps) {
  return (
    <label className={cx("block", className)}>
      {label && (
        <span className={fieldLabelClass}>
          {label}
          {required && <span className="ml-0.5 text-[var(--color-danger)]">*</span>}
        </span>
      )}
      {children}
      {error ? (
        <span className={fieldErrorClass}>{error}</span>
      ) : helper ? (
        <span className={fieldHelperClass}>{helper}</span>
      ) : null}
    </label>
  );
}

interface BaseFieldProps {
  label?: ReactNode;
  helper?: ReactNode;
  error?: ReactNode;
  containerClassName?: string;
}

export type TextFieldProps = BaseFieldProps & InputHTMLAttributes<HTMLInputElement>;

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  ({ label, helper, error, containerClassName, className, type = "text", required, ...rest }, ref) => (
    <FieldFrame
      label={label}
      helper={helper}
      error={error}
      required={required}
      className={containerClassName}
    >
      <input
        ref={ref}
        type={type}
        required={required}
        className={cx(inputBase, "font-sans", className)}
        {...rest}
      />
    </FieldFrame>
  ),
);
TextField.displayName = "TextField";

export const NumberField = forwardRef<HTMLInputElement, TextFieldProps>(
  ({ className, ...rest }, ref) => (
    <TextField
      ref={ref}
      type="number"
      inputMode="decimal"
      className={cx("font-mono tabular-nums", className)}
      {...rest}
    />
  ),
);
NumberField.displayName = "NumberField";

export const DateField = forwardRef<HTMLInputElement, TextFieldProps>(
  ({ className, ...rest }, ref) => (
    <TextField
      ref={ref}
      type="date"
      className={cx("font-mono", className)}
      {...rest}
    />
  ),
);
DateField.displayName = "DateField";

export type SelectFieldProps = BaseFieldProps & SelectHTMLAttributes<HTMLSelectElement>;

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(
  ({ label, helper, error, containerClassName, className, required, children, ...rest }, ref) => (
    <FieldFrame
      label={label}
      helper={helper}
      error={error}
      required={required}
      className={containerClassName}
    >
      <select
        ref={ref}
        required={required}
        className={cx(inputBase, "appearance-none pr-9", className)}
        {...rest}
      >
        {children}
      </select>
    </FieldFrame>
  ),
);
SelectField.displayName = "SelectField";

export type TextareaFieldProps = BaseFieldProps & TextareaHTMLAttributes<HTMLTextAreaElement>;

export const TextareaField = forwardRef<HTMLTextAreaElement, TextareaFieldProps>(
  ({ label, helper, error, containerClassName, className, required, ...rest }, ref) => (
    <FieldFrame
      label={label}
      helper={helper}
      error={error}
      required={required}
      className={containerClassName}
    >
      <textarea
        ref={ref}
        required={required}
        className={cx(inputBase, "min-h-[5rem] resize-y", className)}
        {...rest}
      />
    </FieldFrame>
  ),
);
TextareaField.displayName = "TextareaField";

export interface CheckboxFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: ReactNode;
  helper?: ReactNode;
}

export const CheckboxField = forwardRef<HTMLInputElement, CheckboxFieldProps>(
  ({ label, helper, className, ...rest }, ref) => (
    <label className={cx("flex items-start gap-2", className)}>
      <input
        ref={ref}
        type="checkbox"
        className="mt-0.5 size-4 rounded border-[var(--color-border-default)] text-[var(--color-accent)] focus:ring-[var(--color-accent)]"
        {...rest}
      />
      <span>
        <span className="block text-sm text-[var(--color-text)]">{label}</span>
        {helper && (
          <span className="block text-xs text-[var(--color-muted)]">{helper}</span>
        )}
      </span>
    </label>
  ),
);
CheckboxField.displayName = "CheckboxField";

// ============================================================================
// Tabs
// ----------------------------------------------------------------------------
// Token-driven tab bar with proper aria roles. Render <Tabs> wrapping a list
// of <Tab> trigger buttons; render content panels with <TabPanel>. Caller
// owns the active-key state. Promotes housing.tsx's ad-hoc pattern; future
// Phase 11 polish migrates housing to this primitive.
// ============================================================================

export interface TabLinkSpec {
  readonly to: string;
  readonly label: string;
}

export interface TabsProps {
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly ariaLabel: string;
  readonly children: ReactNode;
  /**
   * Optional link-style tabs appended to the end of the tab strip.
   * Each becomes a NavLink to a sibling URL — used when a related
   * page lives at a separate route but visually belongs in the same
   * navigation strip (e.g. /portfolio/rebalance + /portfolio/stress-test
   * sit next to the in-page tabs of /portfolio).
   */
  readonly trailingLinks?: readonly TabLinkSpec[];
}

export function Tabs({
  value,
  onChange,
  ariaLabel,
  children,
  trailingLinks,
}: TabsProps) {
  return (
    <div data-tabs-value={value}>
      <div
        role="tablist"
        aria-label={ariaLabel}
        className="mb-4 flex flex-wrap gap-1 border-b border-[var(--color-border-subtle)]"
      >
        {Children.map(children, (child) => {
          if (!isValidElement<TabProps>(child)) return null;
          const { value: tabValue, label } = child.props;
          const active = tabValue === value;
          return (
            <button
              key={tabValue}
              type="button"
              role="tab"
              aria-selected={active}
              aria-controls={`tabpanel-${tabValue}`}
              id={`tab-${tabValue}`}
              onClick={() => onChange(tabValue)}
              className={
                active
                  ? "-mb-px border-b-2 border-[var(--color-accent)] px-3 py-2 text-sm font-medium text-[var(--color-text)]"
                  : "-mb-px border-b-2 border-transparent px-3 py-2 text-sm text-[var(--color-muted)] hover:text-[var(--color-text)]"
              }
            >
              {label}
            </button>
          );
        })}
        {trailingLinks?.map((link) => (
          <NavLink
            key={link.to}
            to={link.to}
            className="-mb-px border-b-2 border-transparent px-3 py-2 text-sm text-[var(--color-muted)] hover:text-[var(--color-text)]"
          >
            {link.label} →
          </NavLink>
        ))}
      </div>
      {Children.map(children, (child) => {
        if (!isValidElement<TabProps>(child)) return null;
        if (child.props.value !== value) return null;
        return (
          <div
            role="tabpanel"
            id={`tabpanel-${child.props.value}`}
            aria-labelledby={`tab-${child.props.value}`}
          >
            {child.props.children}
          </div>
        );
      })}
    </div>
  );
}

export interface TabProps {
  readonly value: string;
  readonly label: string;
  readonly children?: ReactNode;
}

/** Render inside <Tabs>. The children become the panel body for `value`. */
export function Tab(_props: TabProps) {
  // Rendering is handled by <Tabs> — this component is purely declarative.
  return null;
}
